import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FaCalendarAlt } from 'react-icons/fa';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { supabase, getUserRole, updateUserRole, getCurrentUser } from '../../services/supabase';
import { useMediaQuery } from 'react-responsive';
import * as XLSX from 'xlsx';
import {  Download, FileSpreadsheet } from 'lucide-react';
import { DefaultButton, PrimaryButton } from '../../components/Common/BaseButton';
import { useNavigate } from 'react-router-dom';
import { getLastSevenDaysRange, getCurrentMonthRange, getInitials, generateCompanyId, generateEmployeeId, generateEventId, formatDateForDisplay, formatTimeForDisplay } from '../../utils/utils';
// import your data functions here

const ExportModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.18);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const ExportModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  min-width: 320px;
  max-width: 90vw;
  box-shadow: 0 2px 16px rgba(0,0,0,0.13);
  position: relative;
`;

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  padding: 2rem;
  margin: 1rem 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: start;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  margin: 0;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0 0;
  color: #888;
`;

const SearchBar = styled.input`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid #ddd;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: none;
  border: 1px solid #ececec;
`;

const TableHeader = styled.th`
  background: none;
  color: #222;
  padding: 1rem 1rem 0.5rem 1rem;
  text-align: left;
  font-weight: 700;
  font-size: 1.05rem;
  border-bottom: 2px solid #ececec;
`;

const TableHeaderSubtitle = styled.div`
  font-size: 0.75rem;
  color: #888;
  font-weight: normal;
  margin-top: 2px;
`;

const TableRow = styled.tr`
  background: white;
  &:not(:last-child) {
    border-bottom: 1px solid #ececec;
  }
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 1.1rem 1rem 1.1rem 1rem;
  border-bottom: 1px solid #ececec;
`;

const TimeDisplay = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: #222;
`;

const TimeMax = styled.span`
  color: #bbb;
  font-size: 0.85rem;
  margin-left: 2px;
`;

const ProgressBar = ({ value, max, isMobile }) => {
    if (isMobile) {
        return (
            <div style={{ background: '#eee', borderRadius: 8, height: 8, width: '100%', marginTop: 4 }}>
                <div
                style={{
        width: `${Math.min((value / max) * 100, 100)}%`,
        background: '#1dbf73',
        height: '100%',
        borderRadius: 8,
        transition: 'width 0.3s'
      }}
    />
                </div>
    );
  }
  return (
    <div style={{ background: '#eee', borderRadius: 8, height: 8, width: 130, marginTop: 4 }}>
      <div
        style={{
          width: `${Math.min((value / max) * 100, 100)}%`,
          background: '#1dbf73',
          height: '100%',
          borderRadius: 8,
          transition: 'width 0.3s'
        }}
      />
    </div>
  );
};

function formatMinutesToHHMM(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

const MobileCard = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  margin-bottom: 1rem;
  padding: 1.2rem;
  @media (min-width: 999px) {
    display: none;
  }
`;

const MobileLabel = styled.div`
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 2px;
`;

const DesktopTableWrapper = styled.div`
  @media (max-width: 1000px) {
    display: none;
  }
`;

const MobileCardsWrapper = styled.div`
  @media (min-width: 999px) {
    display: none;
  }
`;

function formatLocalDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString(); // Only date
}

function formatLocalTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); // e.g., 01:18 PM
}

function formatDuration(minutes) {
  if (!minutes || isNaN(minutes)) return '0 hr';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hrs} hr`;
  if (hrs === 0) return `${mins} min`;
  return `${hrs} hr ${mins} min`;
}

function formatDateForFilename(date) {
  const d = typeof date === 'string' && date.includes('T') 
    ? new Date(date) 
    : new Date(date + 'T00:00:00');
  
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

const CustomSelect = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 0.3rem .6rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1rem;
  color: #333;

  &:hover {
    border-color: #bbb;
  }

  &:focus {
    outline: none;
    border-color: var(--primary-green);
    box-shadow: 0 0 0 2px rgba(45, 136, 45, 0.1);
  }
`;

const SelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-top: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
`;

const SelectOption = styled.div`
  padding: 0.3rem .6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.2s ease;
  will-change: transform;

  &:hover {
    background-color: #f9fafb;
  }

  ${props => props.$selected && `
    background-color: #f0fdf4;
    border-left: 3px solid var(--primary-green);
  `}
`;

const ChevronDown = styled.span`
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #666;
  margin-left: 0.5rem;
`;

const MobileSubtitle = styled.div`
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 2px;
  display: inline-block;
`;

//Lmfaoo Claude 4 went beastmode with this one
const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2D882D 0%, #4CAF50 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2 ease;
  will-change: transform;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  /* Alternative color variations based on first letter */
  &[data-letter="A"], &[data-letter="B"], &[data-letter="C"] {
    background: linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%);
  }
  
  &[data-letter="D"], &[data-letter="E"], &[data-letter="F"] {
    background: linear-gradient(135deg, #4ECDC4 0%, #6EE7DF 100%);
  }
  
  &[data-letter="G"], &[data-letter="H"], &[data-letter="I"] {
    background: linear-gradient(135deg, #45B7D1 0%, #6AC8E8 100%);
  }
  
  &[data-letter="J"], &[data-letter="K"], &[data-letter="L"] {
    background: linear-gradient(135deg, #F39C12 0%, #F7B731 100%);
  }
  
  &[data-letter="M"], &[data-letter="N"], &[data-letter="O"] {
    background: linear-gradient(135deg, #9B59B6 0%, #BB6BD9 100%);
  }
  
  &[data-letter="P"], &[data-letter="Q"], &[data-letter="R"] {
    background: linear-gradient(135deg, #E74C3C 0%, #EC7063 100%);
  }
  
  &[data-letter="S"], &[data-letter="T"], &[data-letter="U"] {
    background: linear-gradient(135deg, #1ABC9C 0%, #48C9B0 100%);
  }
  
  &[data-letter="V"], &[data-letter="W"], &[data-letter="X"], &[data-letter="Y"], &[data-letter="Z"] {
    background: linear-gradient(135deg, #8E44AD 0%, #A569BD 100%);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 0.85rem;
  }
`;

const EmployeesPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return startOfMonth.toISOString().slice(0, 10); // YYYY-MM-DD format for input
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return endOfMonth.toISOString().slice(0, 10); // YYYY-MM-DD format for input
  });
  const [loadingExport, setLoadingExport] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [roleChecked, setRoleChecked] = useState(false);
  const [exportType, setExportType] = useState('events'); // 'events' | 'employees'

  const isMobile = useMediaQuery({ maxWidth: 699 });

  useEffect(() => {
    async function checkRole() {
      const { user } = await getCurrentUser();
      if (user) {
        setCurrentUserId(user.id);
        const { role } = await getUserRole(user.id);
        if (role !== 'manager') {
          navigate('/dashboard', { replace: true });
        }
      } else {
        // Not logged in, redirect to login or dashboard
        navigate('/dashboard', { replace: true });
      }
      setRoleChecked(true);
    }
    checkRole();
  }, [navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch users from your RPC or directly from auth.users
        const { data: usersData, error: usersError } = await supabase.rpc('get_users_with_name');
        if (usersError) throw usersError;

        const userIds = usersData.map(user => user.id);
        // Fetch event counts for each user
        const { data, error } = await supabase.rpc('get_future_event_assignment_counts', {
          user_ids: userIds
        });
        if (error) throw error;

        const counts = {};
        data.forEach(row => {
          counts[row.user_id] = row.future_event_count;
        });

        //get time entry data for each user
        const { data: timeEntriesData, error: timeEntriesError } = await supabase.rpc('get_time_entry_summaries', {
          user_ids: userIds
        });
        if (timeEntriesError) throw timeEntriesError;
            
        // Fetch roles for each user
        const usersWithRoles = await Promise.all(
          usersData.map(async (user) => {
            const { role } = await getUserRole(user.id);
            return {
              ...user,
              user_role: role,
              weeklyMinutes: timeEntriesData.find(entry => entry.user_id === user.id)?.weekly_minutes || 0,
              monthlyMinutes: timeEntriesData.find(entry => entry.user_id === user.id)?.monthly_minutes || 0,
              currentEventAssignments: counts[user.id] || 0,
              lastLogin: user.last_sign_in_at ? new Date(user.last_sign_in_at) : null,
            };
          })
        );
        setUsers(usersWithRoles.sort((a, b) => {
          if (a.id === currentUserId) return -1;
          if (b.id === currentUserId) return 1;
          return b.lastLogin - a.lastLogin;
        }));
      } catch (err) {
        setError(err.message || 'Failed to load employees');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentUserId]); // Add currentUserId to the dependency array

  const filteredUsers = users.filter(user =>
    (user.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleExport = async () => {
    setLoadingExport(true);
    const { data: employees } = await supabase.rpc('get_users_with_name');
    const userIds = employees.map(e => e.id);

    const from = new Date(startDate).toISOString();
    const to = new Date(endDate).toISOString();
    const { data: timeEntries } = await supabase.rpc('get_time_entries_for_users', { user_ids: userIds, from_date: from, to_date: to });


    // 2. Group and format data
    const excelData = [];
    employees.forEach(emp => {
      const entries = timeEntries.filter(e => e.user_id === emp.id);
      if (entries.length > 0) {
        excelData.push({
          Employee: emp.name,
          Email: emp.email,
          Date: '',
          'Start Time': '',
          'End Time': '',
          Hours: '',
          Category: '',
          Location: '',
          Description: '',
        });
        entries.forEach(entry => {
          excelData.push({
            Employee: '',
            Email: '',
            Date: formatLocalDate(entry.date || entry.created_at),
            'Start Time': formatLocalTime(entry.start_time),
            'End Time': formatLocalTime(entry.end_time),
            Hours: entry.duration_minutes ? (entry.duration_minutes / 60).toFixed(2) : '',
            Category: entry.category,
            Location: entry.location,
            Description: entry.description,
          });
        });
        // Total row
        const totalHours = entries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0) / 60;
        excelData.push({
          Employee: '',
          Email: '',
          Date: '',
          'Start Time': '',
          'End Time': '',
          Hours: `Total: ${totalHours.toFixed(2)}`,
          Category: '',
          Location: '',
          Description: '',
        });
        // Blank row for spacing
        excelData.push({ Employee: '', Email: '', Date: '', 'Start Time': '', 'End Time': '', Hours: '', Category: '', Location: '', Description: '' });
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const objectMaxLength = [];
    excelData.forEach(row => {
      Object.keys(row).forEach((key, colIdx) => {
        const value = row[key] ? String(row[key]) : '';
        objectMaxLength[colIdx] = Math.max(objectMaxLength[colIdx] || key.length, value.length);
      });
    });
    worksheet['!cols'] = objectMaxLength.map(w => ({ wch: w + 2 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Time Entries');

    const fromLabel = formatDateForFilename(startDate);
    const toLabel = formatDateForFilename(endDate);
    XLSX.writeFile(workbook, `employee_time_entries_${fromLabel}_to_${toLabel}.xlsx`);
    setLoadingExport(false);
    setShowExportModal(false);
  };

  const handleExportEvents = async () => {
    setLoadingExport(true);
    
    try {
      const from = new Date(startDate).toISOString();
      const to = new Date(endDate).toISOString();

      // Get all events in the date range
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select(`
          *,
          event_employees (*),
          event_contacts (*),
          event_equipment (
            *,
            equipment (*)
          ),
          event_ratings (rating),
          event_issues (issue_type, description)
        `)
        .gte('event_date', from.split('T')[0])
        .lte('event_date', to.split('T')[0])
        .order('event_date', { ascending: true });

      if (eventsError) throw eventsError;

      // Get all unique user IDs from event_employees AND account_rep_id
      const employeeUserIds = [...new Set(
        eventsData.flatMap(event => 
          event.event_employees.map(emp => emp.user_id)
        )
      )];
      
      // Get all account rep IDs (filter out nulls)
      const accountRepIds = [...new Set(
        eventsData
          .map(event => event.account_rep_id)
          .filter(Boolean) // Remove null/undefined values
      )];
      // Combine all user IDs we need to fetch
      const userIds = [...new Set([...employeeUserIds, ...accountRepIds])];
      // Get user information using the RPC function (same as existing code)
      const { data: userData, error: userError } = await supabase
        .rpc('get_users_by_ids_with_name', { user_ids: userIds });

      if (userError) throw userError;

      // Create a map of user data by ID for quick lookup
      const userMap = {};
      if (userData) {
        userData.forEach(user => {
          userMap[user.id] = user;
        });
      }

      // Enrich events with user data
      const enrichedEvents = eventsData.map(event => ({
        ...event,
        event_employees: event.event_employees.map(emp => ({
          ...emp,
          user: userMap[emp.user_id] || { id: emp.user_id }
        })),
        // Add account rep data
        account_rep: event.account_rep_id ? userMap[event.account_rep_id] : null
      }));
      // Get time entries for all events in the date range
      const eventIds = eventsData.map(event => event.id);
      let timeEntriesData = [];
      
      if (eventIds.length > 0) {
        const { data: timeEntries, error: timeEntriesError } = await supabase
          .from('time_entries')
          .select('*')
          .in('event_id', eventIds)
          .gte('start_time', from)
          .lte('end_time', to)
          .order('event_id', { ascending: true })
          .order('start_time', { ascending: true });

        if (timeEntriesError) throw timeEntriesError;
        timeEntriesData = timeEntries || [];
      }

      // Get user data for time entries
      const timeEntryUserIds = [...new Set(timeEntriesData.map(entry => entry.user_id))];
      let timeEntryUserMap = {};
      
      if (timeEntryUserIds.length > 0) {
        const { data: timeEntryUsers, error: timeEntryUsersError } = await supabase
          .rpc('get_users_by_ids_with_name', { user_ids: timeEntryUserIds });

        if (timeEntryUsersError) throw timeEntryUsersError;
        
        if (timeEntryUsers) {
          timeEntryUsers.forEach(user => {
            timeEntryUserMap[user.id] = user;
          });
        }
      }

      // Group time entries by event and user
      const timeEntriesByEventAndUser = {};
      timeEntriesData.forEach(entry => {
        const key = `${entry.event_id}_${entry.user_id}`;
        if (!timeEntriesByEventAndUser[key]) {
          timeEntriesByEventAndUser[key] = [];
        }
        timeEntriesByEventAndUser[key].push({
          ...entry,
          user: timeEntryUserMap[entry.user_id] || { id: entry.user_id }
        });
      });

      // Build Excel data - one row per assigned team member per event
      const excelData = [];

      enrichedEvents.forEach(event => {
        // Process each assigned team member
        event.event_employees.forEach(assignedEmployee => {
          const userId = assignedEmployee.user_id;
          const user = assignedEmployee.user;
          const timeEntryKey = `${event.id}_${userId}`;
          const userTimeEntries = timeEntriesByEventAndUser[timeEntryKey] || [];

          // Generate base event data
          let eventId = '';
          let companyId = '';
          let operatorId = '';
          let salesRepId = '';

          try {
            // Generate Event ID
            if (event.title && event.location && event.id) {
              eventId = generateEventId(event.title, event.location, event.id);
            }
            
            // Generate Company ID
            if (event.company_name) {
              companyId = generateCompanyId(event.company_name);
            }
            
            // Generate Operator ID
            if (user?.email && user?.name) {
              operatorId = generateEmployeeId(user.email, user.name);
            }
            
            // Generate Sales Rep ID
            if (event.account_rep?.email && event.account_rep?.name) {
              const salesRepName = `${event.account_rep.name}`.trim();
              salesRepId = generateEmployeeId(event.account_rep.email, salesRepName);
            }
          } catch (error) {
            console.warn('Error generating IDs:', error);
          }

          // Convert UTC times to event's local timezone
          const eventTimezone = event.timezone || 'UTC';
          const localEventStartTime = event.start_time ? formatTimeForDisplay(event.start_time, eventTimezone) : '';
          const localEventEndTime = event.end_time ? formatTimeForDisplay(event.end_time, eventTimezone) : '';
          const localEventDate = event.start_time ? formatDateForDisplay(event.start_time, eventTimezone) : '';

          // Base event data that's common to all rows
          const baseEventData = {
            'Event ID': eventId,
            'Event Name': event.title,
            'Event Date': localEventDate,
            'Expected Revenue': event.expected_revenue ? parseFloat(event.expected_revenue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
            'Sales Rep': event.account_rep?.name || '',
            'Sales Rep ID': salesRepId || '',
            'Event Start Time': localEventStartTime,
            'Event End Time': localEventEndTime,
            'Event Timezone': eventTimezone,
            "Event Type": event.event_type,
            "Event Venue Type": event.venue_type,
            'Company Name': event.company_name || '',
            'Company ID': companyId,
            'Event Location': event.location,
            'Status': event.event_status,
            'Event Description': event.description || '',
            'Equipment': event.event_equipment?.map(eq => `${eq.equipment?.name} (${eq.quantity})`).join(', ') || '',
            'Event Contact Name': event.event_contacts?.[0]?.name || '',
            'Event Contact Role': event.event_contacts?.[0]?.role || '',
            'Event Contact Phone': event.event_contacts?.[0]?.phone || '',
            'Event Contact Email': event.event_contacts?.[0]?.email || '',
            'Event Rating': event.event_ratings?.map(r => r.rating).join('\n') || '',
            'Event Issues': event.event_issues?.map(i => i.issue_type + (i.description ? ' - ' + i.description : '')).join('\n') || '',
            'Assigned Team Members': event.event_employees?.map(emp => emp.user?.name).join(', ') || '',
            'Operator': user?.name || 'Unknown',
            'Operator ID': operatorId,
          };

          if (userTimeEntries.length > 0) {
            // Add one row per time entry for this user
            userTimeEntries.forEach(entry => {
              excelData.push({
                ...baseEventData,
                'Time Entry Date': formatLocalDate(entry.start_time),
                'Time Start': formatLocalTime(entry.start_time),
                'Time End': formatLocalTime(entry.end_time),
                'Hours': entry.duration_minutes ? (entry.duration_minutes / 60).toFixed(2) : '',
                'Category': entry.category || '',
                'Time Description': entry.description || '',
              });
            });
          } else {
            // No time entries for this assigned team member - add row with "No time entry recorded"
            excelData.push({
              ...baseEventData,
              'Time Entry Date': 'No time entry recorded',
              'Time Start': 'No time entry recorded',
              'Time End': 'No time entry recorded',
              'Hours': 'No time entry recorded',
              'Category': 'No time entry recorded',
              'Time Description': 'No time entry recorded',
            });
          }
        });

        // If event has no assigned team members, still show the event
        if (event.event_employees.length === 0) {
          let eventId = '';
          let companyId = '';
          let salesRepId = '';

          try {
            // Generate Event ID
            if (event.title && event.location && event.id) {
              eventId = generateEventId(event.title, event.location, event.id);
            }
            
            // Generate Company ID
            if (event.company_name) {
              companyId = generateCompanyId(event.company_name);
            }
            
            // Generate Sales Rep ID
            if (event.account_rep?.email && event.account_rep?.name) {
              const salesRepName = `${event.account_rep.name}`.trim();
              salesRepId = generateEmployeeId(event.account_rep.email, salesRepName);
            }
          } catch (error) {
            console.warn('Error generating IDs:', error);
          }

          // Convert UTC times to event's local timezone
          const eventTimezone = event.timezone || 'UTC';
          const localEventStartTime = event.start_time ? formatTimeForDisplay(event.start_time, eventTimezone) : '';
          const localEventEndTime = event.end_time ? formatTimeForDisplay(event.end_time, eventTimezone) : '';
          const localEventDate = event.start_time ? formatDateForDisplay(event.start_time, eventTimezone) : '';

          excelData.push({
            'Event ID': eventId,
            'Event Name': event.title,
            'Event Date': localEventDate,
            'Expected Revenue': event.expected_revenue ? parseFloat(event.expected_revenue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
            'Sales Rep': event.account_rep?.name || '',
            'Sales Rep ID': salesRepId || '',
            'Event Start Time': localEventStartTime,
            'Event End Time': localEventEndTime,
            'Event Timezone': eventTimezone,
            'Event Type': event.event_type,
            'Event Venue Type': event.venue_type,
            'Company Name': event.company_name || '',
            'Company ID': companyId,
            'Event Location': event.location,
            'Status': event.event_status,
            'Event Description': event.description || '',
            'Equipment': event.event_equipment?.map(eq => `${eq.equipment?.name} (${eq.quantity})`).join(', ') || '',
            'Event Contact Name': '',
            'Event Contact Role': '',
            'Event Contact Phone': '',
            'Event Contact Email': '',
            'Event Rating': event.event_ratings?.map(r => r.rating).join('\n') || '',
            'Event Issues': event.event_issues?.map(i => i.issue_type + (i.description ? ' - ' + i.description : '')).join('\n') || '',
            'Assigned Team Members': '',
            'Operator': '',
            'Operator ID': '',
            'Time Entry Date': '',
            'Time Start': '',
            'Time End': '',
            'Hours': '',
            'Category': '',
            'Time Description': '',
          });
        }
      });

      // Get time entries that are NOT associated with any event in the date range
      const { data: unassociatedTimeEntries, error: unassociatedError } = await supabase
        .from('time_entries')
        .select('*')
        .is('event_id', null) // No event associated
        .gte('start_time', from)
        .lte('end_time', to)
        .order('start_time', { ascending: true });

      if (unassociatedError) throw unassociatedError;

      // Get user data for unassociated time entries
      if (unassociatedTimeEntries && unassociatedTimeEntries.length > 0) {
        const unassociatedUserIds = [...new Set(unassociatedTimeEntries.map(entry => entry.user_id))];
        let unassociatedUserMap = {};
        
        if (unassociatedUserIds.length > 0) {
          const { data: unassociatedUsers, error: unassociatedUsersError } = await supabase
            .rpc('get_users_by_ids_with_name', { user_ids: unassociatedUserIds });

          if (unassociatedUsersError) throw unassociatedUsersError;
          
          if (unassociatedUsers) {
            unassociatedUsers.forEach(user => {
              unassociatedUserMap[user.id] = user;
            });
          }
        }

        // Add unassociated time entries to the Excel data
        unassociatedTimeEntries.forEach(entry => {
          const user = unassociatedUserMap[entry.user_id] || { id: entry.user_id };

          excelData.push({
            'Event Name': 'No Event Associated',
            'Event Date': '',
            'Expected Revenue': '',
            'Sales Rep': '',
            'Event Start Time': '',
            'Event End Time': '',
            'Event Type': '',
            'Event Venue Type': '',
            'Company Name': '',
            'Company ID': '',
            'Event Location': entry.location || '',
            'Status': '',
            'Event Description': '',
            'Equipment': '',
            'Event Contact Name': '',
            'Event Contact Role': '',
            'Event Contact Phone': '',
            'Event Contact Email': '',
            'Event Rating': '',
            'Event Issues': '',
            'Assigned Team Members': '',
            'Operator': user?.name || 'Unknown',
            'Operator ID': user?.email && user?.name ? generateEmployeeId(user.email, user.name) : '',
            'Time Entry Date': formatLocalDate(entry.start_time),
            'Time Start': formatLocalTime(entry.start_time),
            'Time End': formatLocalTime(entry.end_time),
            'Hours': entry.duration_minutes ? (entry.duration_minutes / 60).toFixed(2) : '',
            'Category': entry.category || '',
            'Time Description': entry.description || '',
          });
        });
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Auto-size columns
      const objectMaxLength = [];
      excelData.forEach(row => {
        Object.keys(row).forEach((key, colIdx) => {
          const value = row[key] ? String(row[key]) : '';
          objectMaxLength[colIdx] = Math.max(objectMaxLength[colIdx] || key.length, value.length);
        });
      });
      worksheet['!cols'] = objectMaxLength.map(w => ({ wch: Math.min(w + 2, 50) })); // Cap at 50 chars

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Event Time Reports');

      const fromLabel = formatDateForFilename(startDate);
      const toLabel = formatDateForFilename(endDate);
      XLSX.writeFile(workbook, `event_time_reports_${fromLabel}_to_${toLabel}.xlsx`);

    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setLoadingExport(false);
      setShowExportModal(false);
    }
  };

    // Scroll prevention for export modal
    useEffect(() => {
      if (showExportModal) {
        // Prevent body scrolling when modal is open
        document.body.style.overflow = 'hidden';
      } else {
        // Restore body scrolling when modal is closed
        document.body.style.overflow = 'unset';
      }
  
      // Cleanup function to restore scrolling when component unmounts
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [showExportModal]);  

  if (!roleChecked) return null; // or a spinner

  if (loading) return <PageContainer><LoadingSpinner text="Loading Employees..." /></PageContainer>

  if (error) return <PageContainer>Error: {error}</PageContainer>;

  const handleRoleUpdate = async (userId, newRole) => {
    setUsers(users =>
      users.map(u =>
        u.id === userId ? { ...u, updatingRole: true } : u
      )
    );
    
    const { error } = await updateUserRole(userId, newRole);
    
    setUsers(users =>
      users.map(u =>
        u.id === userId
          ? { 
              ...u, 
              user_role: error ? u.user_role : newRole, 
              updatingRole: false, 
            }
          : u
      )
    );
    
    if (error) {
      console.error('Failed to update role:', error);
      alert(error.message || 'Failed to update role');
    }
  };

  return (
    <Card>
      <HeaderRow>
        <div>
          <Title>Employee Directory</Title>
          <Subtitle>View and manage your team members</Subtitle>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.5rem' }}>
          <SearchBar
            placeholder="Search employees..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <PrimaryButton
            style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem' }}
            onClick={() => setShowExportModal(true)}
          >
            <FileSpreadsheet size={18} />
            Export
          </PrimaryButton>
        </div>
      </HeaderRow>

      {showExportModal && createPortal(
        <ExportModal onClick={() => setShowExportModal(false)}>
          <ExportModalContent onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
              <FileSpreadsheet size={20} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Export Employee Data
            </div>
            <div style={{ color: '#888', fontSize: 14, marginBottom: 16 }}>
              Choose export format and date range
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 6 }}>Export Type</div>
              <select 
                value={exportType} 
                onChange={e => setExportType(e.target.value)} 
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd', marginBottom: 12 }}
              >
                <option value="events">Event-Based Time Report</option>
                <option value="employees">Employee Time Report</option>
              </select>
              
              <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                {exportType === 'events' 
                  ? 'Groups time entries by event with full event details, equipment, and contacts'
                  : 'Traditional employee-focused time tracking report'
                }
              </div>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 6 }}>Event Date Range</div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : '1fr 1fr', 
                gap: 8, 
                marginBottom: 8 
              }}>
                <div>
                  <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Start Date</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={e => setStartDate(e.target.value)} 
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 6, 
                      border: '1px solid #ddd',
                      fontSize: 14
                    }} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>End Date</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={e => setEndDate(e.target.value)} 
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 6, 
                      border: '1px solid #ddd',
                      fontSize: 14
                    }} 
                  />
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <DefaultButton onClick={() => setShowExportModal(false)}>
                Cancel
              </DefaultButton>
              <PrimaryButton onClick={exportType === 'events' ? handleExportEvents : handleExport} disabled={loadingExport}>
                <Download size={18} />
                {loadingExport ? "Exporting..." : "Export"}
              </PrimaryButton>
            </div>
          </ExportModalContent>
        </ExportModal>,
        document.body
      )}

      {/* Desktop Table */}
      <DesktopTableWrapper>
        <Table>
          <thead>
            <tr>
              <TableHeader>Employee</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader>
                Last 7 Days
                <TableHeaderSubtitle>{getLastSevenDaysRange().formatted}</TableHeaderSubtitle>
              </TableHeader>
              <TableHeader>
                This Month
                <TableHeaderSubtitle>{getCurrentMonthRange().formatted}</TableHeaderSubtitle>
              </TableHeader>
              <TableHeader>Upcoming Events</TableHeader>
              <TableHeader>Last Login</TableHeader>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <TableRow key={user.id}>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <UserAvatar data-letter={user.name ? user.name.charAt(0).toUpperCase() : '?'}>
                      {getInitials(user.name)}
                    </UserAvatar>
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {user.name || 'N/A'}
                        {user.id === currentUserId && (
                          <span style={{ marginLeft: 8, color: '#666', fontSize: '0.9em' }}>(You)</span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: '#888' }}>{user.email}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CustomSelect>
                      <SelectButton 
                        type="button"
                        onClick={() => {
                          if (user.id === currentUserId) return; // Prevent opening dropdown for current user
                          setUsers(users =>
                            users.map(u =>
                              u.id === user.id ? { ...u, isRoleDropdownOpen: !u.isRoleDropdownOpen } : u
                            )
                          );
                        }}
                        style={{ 
                          opacity: user.id === currentUserId ? 0.5 : 1,
                          cursor: user.id === currentUserId ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {user.user_role === 'manager' ? 'Manager' : 'Employee'}
                        {user.id !== currentUserId && <ChevronDown />}
                      </SelectButton>
                      {user.isRoleDropdownOpen && user.id !== currentUserId && (
                        <SelectDropdown>
                          <SelectOption
                            $selected={user.user_role === 'employee'}
                            onClick={() => {
                              if (user.user_role === 'employee') return;
                              handleRoleUpdate(user.id, 'employee');
                            }}
                          >
                            Employee
                          </SelectOption>
                          <SelectOption
                            $selected={user.user_role === 'manager'}
                            onClick={() => {
                              if (user.user_role === 'manager') return;
                              handleRoleUpdate(user.id, 'manager');
                            }}
                          >
                            Manager
                          </SelectOption>
                        </SelectDropdown>
                      )}
                    </CustomSelect>
                    {user.updatingRole && (
                      <div style={{ width: 16, height: 16 }}>
                        <LoadingSpinner size="small" />
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 24 }}>
                    <TimeDisplay style={{ color: user.weeklyMinutes ? '#222' : '#bbb' }}>
                      {formatMinutesToHHMM(user.weeklyMinutes)}
                    </TimeDisplay>
                    <TimeMax>/ 40hr</TimeMax>
                  </div>
                  <ProgressBar value={(user.weeklyMinutes || 0) / 60} max={40} />
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 24 }}>
                    <TimeDisplay style={{ color: user.monthlyMinutes ? '#222' : '#bbb' }}>
                      {formatMinutesToHHMM(user.monthlyMinutes)}
                    </TimeDisplay>
                    <TimeMax>/ 160hr</TimeMax>
                  </div>
                  <ProgressBar value={(user.monthlyMinutes || 0) / 60} max={160} />
                </TableCell>
                <TableCell>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FaCalendarAlt color="#1dbf73" />
                    {user.currentEventAssignments}
                  </span>
                </TableCell>
                <TableCell>
                  {user.lastLogin
                    ? user.lastLogin.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })
                    : 'Never'}
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </DesktopTableWrapper>

      {/* Mobile Cards */}
      <MobileCardsWrapper>
        {filteredUsers.map(user => (
          <MobileCard key={user.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <UserAvatar data-letter={user.name ? user.name.charAt(0).toUpperCase() : '?'}>
                {getInitials(user.name)}
              </UserAvatar>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {user.name || 'N/A'}
                  {user.id === currentUserId && (
                    <span style={{ marginLeft: 8, color: '#666', fontSize: '0.9em' }}>(You)</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#888' }}>{user.email}</div>
              </div>
            </div>
            <MobileLabel>Role</MobileLabel>
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CustomSelect>
                  <SelectButton 
                    type="button"
                    onClick={() => {
                      if (user.id === currentUserId) return; // Prevent opening dropdown for current user
                      setUsers(users =>
                        users.map(u =>
                          u.id === user.id ? { ...u, isRoleDropdownOpen: !u.isRoleDropdownOpen } : u
                        )
                      );
                    }}
                    style={{ 
                      opacity: user.id === currentUserId ? 0.5 : 1,
                      cursor: user.id === currentUserId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {user.user_role === 'manager' ? 'Manager' : 'Employee'}
                    {user.id !== currentUserId && <ChevronDown />}
                  </SelectButton>
                  {user.isRoleDropdownOpen && user.id !== currentUserId && (
                    <SelectDropdown>
                      <SelectOption
                        $selected={user.user_role === 'employee'}
                        onClick={() => {
                          if (user.user_role === 'employee') return;
                          handleRoleUpdate(user.id, 'employee');
                        }}
                      >
                        Employee
                      </SelectOption>
                      <SelectOption
                        $selected={user.user_role === 'manager'}
                        onClick={() => {
                          if (user.user_role === 'manager') return;
                          handleRoleUpdate(user.id, 'manager');
                        }}
                      >
                        Manager
                      </SelectOption>
                    </SelectDropdown>
                  )}
                </CustomSelect>
                {user.updatingRole && (
                  <div style={{ width: 16, height: 16 }}>
                    <LoadingSpinner size="small" />
                  </div>
                )}
              </div>
            </div>
            <MobileLabel style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
              <span>Last 7 Days</span>
              <MobileSubtitle style={{ marginBottom: 0 }}>({getLastSevenDaysRange().formatted})</MobileSubtitle>
            </MobileLabel>
            <div style={{ marginBottom: 8 }}>
              <TimeDisplay style={{ color: user.weeklyMinutes ? '#222' : '#bbb' }}>
                {formatMinutesToHHMM(user.weeklyMinutes)}
              </TimeDisplay>
              <TimeMax>/ 40hr</TimeMax>
              <ProgressBar value={(user.weeklyMinutes || 0) / 60} max={40} isMobile={isMobile} />
            </div>
            {/* <MobileLabel>Monthly Hours</MobileLabel>
            <div style={{ marginBottom: 8 }}>
              <TimeDisplay style={{ color: user.monthlyMinutes ? '#222' : '#bbb' }}>
                {formatMinutesToHHMM(user.monthlyMinutes)}
              </TimeDisplay>
              <TimeMax>/ 160hr</TimeMax>
              <ProgressBar value={(user.monthlyMinutes || 0) / 60} max={160} isMobile={isMobile} />
            </div> */}
            <MobileLabel>Upcoming Events</MobileLabel>
            <div style={{ marginBottom: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FaCalendarAlt color="#1dbf73" />
                {user.currentEventAssignments}
              </span>
            </div>
            <MobileLabel>Last Login</MobileLabel>
            <div>
              {user.lastLogin
                ? user.lastLogin.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })
                : 'Never'}
            </div>
          </MobileCard>
        ))}
      </MobileCardsWrapper>
    </Card>
  );
};

export default EmployeesPage;
